import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnTypeLabels, isValidTitle, TableAdapter } from '@makyoapp/makyo-common'

import { IDBTable } from '@/core/db'

/**
 * prefix to generate the name, optimized for single column add
 */
export function generateColumnNameV2(prefix: string, table: TableAdapter | IDBTable, editId?: string): string {
  const tableColumns = 'columns' in table ? table.columns : table.GetColumns()

  const columnNames = new Set(tableColumns.filter((c) => c.id !== editId).map((c) => c.name.toLowerCase()))

  let name = prefix
  if (!columnNames.has(name.toLowerCase())) {
    return name
  }

  let counter = 2
  name = `${prefix} ${counter}`

  while (columnNames.has(name.toLowerCase())) {
    counter++
    name = `${prefix} ${counter}`
  }

  return name
}

/**
 * prefix to generate the name
 * - specific for multiple lookup column add
 */
export function generateLookupColumnName(prefix: string, startCount = 1, table: TableAdapter, editId?: string): string {
  let name: string

  if (startCount === 1) {
    name = prefix
  } else {
    name = `${prefix} ${startCount}`
  }

  const duplicate = table.columns.find((c) => c.name.toLowerCase() === name.toLowerCase() && c.id !== editId)

  if (duplicate) {
    return generateLookupColumnName(prefix, startCount + 1, table, editId)
  }

  return name
}

export function dataURLtoFile(data: string, filename: string) {
  const arr = data.split(','),
    mime = arr?.[0].match(/:(.*?);/)?.[1],
    bstr = window.atob(arr[1])

  let n = bstr.length
  const u8arr = new Uint8Array(n)

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }

  const ext = mime?.split('/')?.[0] || 'jpeg'

  return new File([u8arr], `${filename}.${ext}`, { type: mime })
}

export function validateTableName(name: string, tables: TableAdapter[]): string {
  if (name.length < 1 || !isValidTitle(name)) {
    return 'Invalid table name'
  } else {
    const fn = tables.find((t) => t.name === name)
    if (fn) {
      return 'Table with this name already exists'
    } else {
      return ''
    }
  }
}

export function colTypeToLabel(colType: ColumnTypes): ColumnTypeLabels {
  switch (colType) {
    case ColumnTypes.Text:
      return ColumnTypeLabels.Text
    case ColumnTypes.URL:
      return ColumnTypeLabels.URL
    case ColumnTypes.Email:
      return ColumnTypeLabels.Email
    case ColumnTypes.Phone:
      return ColumnTypeLabels.Phone
    case ColumnTypes.RichText:
      return ColumnTypeLabels.RichText
    case ColumnTypes.RowID:
      return ColumnTypeLabels.RowID
    case ColumnTypes.Emoji:
      return ColumnTypeLabels.Emoji
    case ColumnTypes.Icon:
      return ColumnTypeLabels.Icon
    case ColumnTypes.QRCode:
      return ColumnTypeLabels.QRCode
    case ColumnTypes.Barcode:
      return ColumnTypeLabels.Barcode
    case ColumnTypes.Slug:
      return ColumnTypeLabels.Slug
    case ColumnTypes.Color:
      return ColumnTypeLabels.Color
    case ColumnTypes.Address:
      return ColumnTypeLabels.Address
    case ColumnTypes.AISEO:
      return ColumnTypeLabels.AISEO
    case ColumnTypes.AITextProcessing:
      return ColumnTypeLabels.AITextProcessing
    case ColumnTypes.Number:
      return ColumnTypeLabels.Number
    case ColumnTypes.Rating:
      return ColumnTypeLabels.Rating
    case ColumnTypes.Boolean:
      return ColumnTypeLabels.Boolean
    case ColumnTypes.DateTime:
      return ColumnTypeLabels.DateTime
    case ColumnTypes.MultiSelect:
      return ColumnTypeLabels.MultiSelect
    case ColumnTypes.Image:
      return ColumnTypeLabels.Image
    case ColumnTypes.Attachment:
      return ColumnTypeLabels.Attachment
    case ColumnTypes.Coordinate:
      return ColumnTypeLabels.Coordinate
    case ColumnTypes.Connection:
      return ColumnTypeLabels.Connection
    case ColumnTypes.Function:
      return ColumnTypeLabels.Function
    case ColumnTypes.Formula:
      return ColumnTypeLabels.Formula
    case ColumnTypes.Lookup:
      return ColumnTypeLabels.Lookup
    case ColumnTypes.Array:
      return ColumnTypeLabels.Array
    case ColumnTypes.ImageArray:
      return ColumnTypeLabels.ImageArray
    case ColumnTypes.Join:
      return ColumnTypeLabels.Join
    case ColumnTypes.Math:
      return 'Math' as ColumnTypeLabels
    case ColumnTypes.Template:
      return 'Template' as ColumnTypeLabels
    case ColumnTypes.SingleCell:
      return 'Single Cell' as ColumnTypeLabels
    default:
      return ColumnTypeLabels.Generic
  }
}
